/* global FinvizQuoteTickers */
import * as dateFns from 'date-fns'
import { ChangeEvent, Component, FormEvent } from 'react'
import ReactDOM from 'react-dom'

import { DatePicker } from '../../main/components/date-picker'
import { ChartLayout, Quote, Utils } from '../charts-lib'
import { getStaticChartConfig } from '../shared/chartLayoutConfig'
import { SpecificChartFunctionality } from '../shared/chartLayoutConfig'
import { ready } from '../shared/ready'
import { getSanitizedTicker } from '../shared/ticker-sanitizer'
import { formatDateToStringUS } from '../shared/utils'

var colorScale = function (value: number) {
  if (value < 0) {
    return '#aa0000'
  } else if (value > 0) {
    return '#008800'
  }
  return '#000'
}

ready(function () {
  var monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  var arraysEqual = function (arr1: unknown[], arr2: unknown[]) {
    if (arr1.length !== arr2.length) {
      return false
    }
    for (var i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false
      }
    }
    return true
  }

  interface ChartProps {
    tickers: string[]
    fromDay: string
    toDay: string
  }

  class Chart extends Component<ChartProps> {
    shouldComponentUpdate(nextProps: ChartProps) {
      return !arraysEqual(this.props.tickers, nextProps.tickers)
    }

    render() {
      const tickerQuote = Quote.select((q) => q.close.length > 0 && this.props.tickers.includes(q.ticker))
      const chartConfig = getStaticChartConfig({
        ticker: tickerQuote[0]?.ticker ?? '',
        timeframe: 'd',
        paneElements: [
          {
            type: 'charts/perf_chart',
            tickers: this.props.tickers,
            fromDate: this.props.fromDay,
            toDate: this.props.toDay,
            overlays: [],
          },
        ],
        specificChartFunctionality: SpecificChartFunctionality.quotePage,
      })
      return (
        <div id="chart" style={{ width: 800 }}>
          <ChartLayout config={chartConfig} />
        </div>
      )
    }
  }

  interface TableProps {
    months: string[]
    rows: Array<{
      ticker: string
      months: string[]
      total: string
    }>
  }

  // mozno vyfarbovat tabulku?
  class Table extends Component<TableProps> {
    render() {
      var { months, rows } = this.props,
        groupsCount = Math.ceil(months.length / 12)
      rows = rows.slice()

      var trs = []
      for (let g = 0; g < groupsCount; g++) {
        trs.push(
          <tr key={g} className="table-dark-row">
            <th className="body-table-nw"></th>
            {months.splice(0, 12).map((month) => (
              <th key={month} className="body-table-nw">
                {month}
              </th>
            ))}
            {g === groupsCount - 1 && <th className="body-table-nw">Total</th>}
          </tr>
        )
        rows.forEach((row, i) => {
          trs.push(
            <tr key={`${g}-${i}`} className={i % 2 === 0 ? 'table-light-row' : 'table-dark-row'}>
              <td className="body-table-nw" style={{ textAlign: 'left' }}>
                {row.ticker}
              </td>
              {row.months.splice(0, 12).map((col, monthIndex) => (
                <td
                  key={`${row.ticker}-${col}-${monthIndex}`}
                  className="body-table-nw"
                  style={{ textAlign: 'right', color: colorScale(parseFloat(col)) }}
                >
                  {col}
                </td>
              ))}
              {g === groupsCount - 1 && (
                <td className="body-table-nw" style={{ textAlign: 'right', color: colorScale(parseFloat(row.total)) }}>
                  {row.total}
                </td>
              )}
            </tr>
          )
        })
      }

      return (
        <table
          className="table-perf"
          cellPadding="3"
          cellSpacing="1"
          // @ts-ignore
          border="0"
        >
          <tbody>{trs}</tbody>
        </table>
      )
    }
  }

  interface PerfState {
    tickersInput: string
    tickers: string[]
    months: Array<{
      value: string
      label: string
    }>
    fromMonth: {
      value: string
      label: string
    }
    toMonth: {
      value: string
      label: string
    }
    minDate: string
    maxDate: string
    fromDay: string
    toDay: string
    loading: boolean
  }

  class Perf extends Component<any, PerfState> {
    constructor(props: any) {
      super(props)
      var now = new Date(),
        months = []
      for (var year = 2009; year <= now.getFullYear(); year++) {
        for (var month = 1; month <= 12; month++) {
          if (year === now.getFullYear() && month > now.getMonth() + 1) {
            break
          }
          months.push({
            value: month.toString().padStart(2, '0') + '/01/' + year,
            label: monthNames[month - 1] + ' ' + year.toString().substring(2),
          })
        }
      }

      this.state = {
        tickersInput: FinvizQuoteTickers.join(','),
        tickers: FinvizQuoteTickers,
        months: months,
        fromMonth: months[months.length - 12],
        toMonth: months[months.length - 1],
        minDate: formatDateToStringUS(dateFns.subYears(new Date(), 9)),
        maxDate: formatDateToStringUS(new Date()),
        fromDay: formatDateToStringUS(dateFns.subYears(new Date(), 1)),
        toDay: formatDateToStringUS(new Date()),
        loading: true,
      }
    }

    render() {
      if (this.state.loading) {
        return <div style={{ fontFamily: 'Verdana', fontSize: 12 }}>Loading...</div>
      }

      var from = new Date(this.state.fromMonth.value)
      var to = new Date(this.state.toMonth.value)
      var monthsToShow = this.state.months
        .map((month) => month.value)
        .filter((month) => new Date(month) >= from && new Date(month) <= to)

      var quotes = Quote.select((q: any) => q.timeframe === 'm')
      var quotesPerf = quotes.map((q: any) => {
        var obj: { ticker: string; perf: Record<string, number> } = { ticker: q.ticker, perf: {} }
        q.date.forEach((d: any, i: number) => {
          if (i === 0) {
            return
          }
          var date = Utils.dateFromUnixTimestamp(d)
          var perf = (q.close[i] * 100) / q.close[i - 1] - 100
          obj.perf[(date.getMonth() + 1).toString().padStart(2, '0') + '/01/' + date.getFullYear()] = perf
        })
        return obj
      })

      var months = monthsToShow.map(
        (month) => monthNames[new Date(month).getMonth()] + ' ' + new Date(month).getFullYear().toString().substring(2)
      )
      var rows = quotesPerf.map((quote: any) => {
        var cols: string[] = []
        var total = 1
        monthsToShow.forEach((month) => {
          var perf = Math.round(quote.perf[month] * 100) / 100
          if (!isNaN(perf)) {
            total *= quote.perf[month] / 100 + 1
            cols.push((perf > 0 ? '+' : '') + perf.toFixed(2) + '%')
          } else {
            cols.push('N/A')
          }
        })
        total = (total - 1) * 100
        return { ticker: quote.ticker, months: cols, total: (total > 0 ? '+' : '') + total.toFixed(2) + '%' }
      })

      return (
        <div style={{ position: 'relative', width: 900 }} className="perf-page">
          <form onSubmit={this._onSubmit} style={{ marginBottom: 20 }}>
            <table style={{ width: '100%', fontFamily: 'Verdana', fontSize: 12 }}>
              <tbody>
                <tr>
                  <td>Tickers:</td>
                  <td width="100%" style={{ padding: '0 5px' }}>
                    <input
                      type="text"
                      value={this.state.tickersInput}
                      onChange={(e) => {
                        this.setState({ tickersInput: e.target.value })
                      }}
                      style={{ width: '100%' }}
                      data-testid="quote-compare-perf-tickers-input"
                    />
                  </td>
                  <td>
                    <input type="submit" value="Submit" data-testid="quote-compare-perf-tickers-input-submit-btn" />
                  </td>
                </tr>
              </tbody>
            </table>
          </form>

          <Chart tickers={this.state.tickers} fromDay={this.state.fromDay} toDay={this.state.toDay} />

          <form
            className="quote-range"
            style={{ position: 'absolute', top: 48, right: 50, fontSize: 13 }}
            data-testid="quote-compare-perf-quote-range-form"
          >
            <div style={{ position: 'relative', width: 80, display: 'inline-block' }}>
              <DatePicker
                minDate={new Date(this.state.minDate)}
                maxDate={new Date(this.state.maxDate)}
                value={new Date(this.state.fromDay)}
                onChange={(newValue: Date) => {
                  this._onDatePickerChange('from', formatDateToStringUS(newValue))
                }}
              >
                <input type="input" readOnly className="w-full" value={this.state.fromDay} />
              </DatePicker>
            </div>
            {' - '}
            <div style={{ position: 'relative', width: 80, display: 'inline-block' }}>
              <DatePicker
                minDate={new Date(this.state.minDate)}
                maxDate={new Date(this.state.maxDate)}
                value={new Date(this.state.toDay)}
                onChange={(newValue: Date) => {
                  this._onDatePickerChange('to', formatDateToStringUS(newValue))
                }}
              >
                <input type="input" readOnly className="w-full" value={this.state.toDay} />
              </DatePicker>
            </div>
          </form>

          <h2
            style={{ fontFamily: 'Verdana', fontSize: 14 }}
            data-testid="quote-compare-perf-monthly-performance-container"
          >
            Monthly performance from&nbsp;&nbsp;
            <select
              value={this.state.fromMonth.value}
              onChange={this._onMonthChange.bind(this, 'from')}
              style={{ fontWeight: 'normal' }}
            >
              {this.state.months.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            &nbsp;&nbsp;to&nbsp;&nbsp;
            <select
              value={this.state.toMonth.value}
              onChange={this._onMonthChange.bind(this, 'to')}
              style={{ fontWeight: 'normal' }}
            >
              {this.state.months.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </h2>
          <Table months={months} rows={rows} />

          <br />
        </div>
      )
    }

    componentDidMount() {
      this._fetch(this.state.tickers, this.state.fromDay, this.state.toDay)
    }

    _fetch = async (tickers: string[], fromDay: string, toDay: string) => {
      this.setState({ loading: true })
      var d = tickers.map((ticker) => ({ ticker: ticker, timeframe: 'd', instrument: 'stock' }))
      var m = tickers.map((ticker) => ({ ticker: ticker, timeframe: 'm', instrument: 'stock' }))
      Quote.deleteAll()
      await Quote.getAll(d.concat(m))
      var dailyQuotes = Quote.select((q: any) => q.timeframe === 'd')
      var fromDate = new Date(fromDay + ' 00:00')
      var toDate = new Date(toDay + ' 23:59')
      dailyQuotes.forEach((q: any) => {
        var o = [],
          h = [],
          l = [],
          c = [],
          d = [],
          v = []
        for (var i = 0; i < q.close.length; i++) {
          var date = Utils.dateFromUnixTimestamp(q.date[i])
          if (fromDate <= date && date <= toDate) {
            o.push(q.open[i])
            h.push(q.high[i])
            l.push(q.low[i])
            c.push(q.close[i])
            d.push(q.date[i])
            v.push(q.volume[i])
          }
        }
        q.open = o
        q.high = h
        q.low = l
        q.close = c
        q.date = d
        q.volume = v
        q.save()
      })

      this.setState({
        loading: false,
      })
    }

    _onSubmit = (e: FormEvent) => {
      e.preventDefault()
      const tickersInput = getSanitizedTicker(this.state.tickersInput, true)
      const tickers = tickersInput.split(',')
      this.setState({
        tickers,
        tickersInput,
      })
      this._fetch(tickers, this.state.fromDay, this.state.toDay)
    }

    _onMonthChange = (type: string, e: ChangeEvent<HTMLSelectElement>) => {
      var state: any = {}
      state[type + 'Month'] = this.state.months.filter((m) => m.value === e.target.value)[0]
      this.setState(state)
    }

    _onDatePickerChange = (type: string, date: any) => {
      var formattedDate = formatDateToStringUS(new Date(date))

      var state: any = {}
      state[type + 'Day'] = formattedDate
      state[type + 'DatePickerOpen'] = false
      this.setState(state)

      if (type === 'from') {
        this._fetch(this.state.tickers, formattedDate, this.state.toDay)
      } else {
        this._fetch(this.state.tickers, this.state.fromDay, formattedDate)
      }
    }

    _hideDatePicker = (type: string) => {
      var state: any = {}
      state[type + 'DatePickerOpen'] = false
      this.setState(state)
    }
  }

  if (document.getElementById('chart') && window['FinvizQuoteTickers']) {
    ReactDOM.render(<Perf />, document.getElementById('chart'))
  }
})
